import { useEffect, useRef } from 'react';
import { Header, UMapBase, useCustomLayer, useLayerStore, useMapStore } from '@uofu-uss-gis/ui-react';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import UParkSidebar from './components/UParkSidebar/UParkSidebar';
import EventParkingPanel from './components/EventParkingPanel/EventParkingPanel';
import { ParkingLotController } from './components/ParkingLotController';
import { ParkingSearchSource } from './components/ParkingSearchSource';
import { EventParkingSearchSources } from './components/EventParkingSearchSources';
import {
  buildEventParkingLayerUrl,
  EVENT_PARKING_LAYER_CONFIG,
  getEventSearchMapLayerId,
} from './config/eventParkingConfig';
import { createEventParkingPopupTemplate } from './utils/eventParkingPopupTemplate';
import { fetchActiveEventNames, resolveEnabledLayersFromActiveNames } from './utils/eventParkingUtils';
import { useEventVisibilityStore } from './store/eventVisibilityStore';

//Force update library: 2

/**
 * DEV FLAG — set to `true` to bypass the Event Names table query and show all
 * event layers in the sidebar regardless of what is active in the service.
 * Set back to `false` to restore the normal remote-driven visibility behaviour.
 */
const DEV_ENABLE_ALL_EVENTS = false;

// Shared popup template instance for all event parking layers
const eventParkingPopupTemplate = createEventParkingPopupTemplate();
const EVENT_TOGGLE_ZOOM_EXPAND = 1;

//Individual event parking layers
function WbbParkingLayer() {
  useCustomLayer({
    layerId: 'wbbParking',
    mapLayerId: 'wbbEventParkingLayer',
    url: buildEventParkingLayerUrl(10) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function MbbParkingLayer() {
  const cfg = EVENT_PARKING_LAYER_CONFIG.find((c) => c.layerId === 'mbbParking')!;
  const indices = Array.isArray(cfg.serviceLayerIndex) ? cfg.serviceLayerIndex : [cfg.serviceLayerIndex];
  const mapLayerIds = Array.isArray(cfg.mapLayerId) ? cfg.mapLayerId : [cfg.mapLayerId];

  useCustomLayer({
    layerId: 'mbbParking',
    mapLayerId: mapLayerIds[0],
    url: buildEventParkingLayerUrl(indices[0]) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  useCustomLayer({
    layerId: 'mbbParking',
    mapLayerId: mapLayerIds[1] ?? mapLayerIds[0],
    url: buildEventParkingLayerUrl(indices[1] ?? indices[0]) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function CommencementParkingLayer() {
  useCustomLayer({
    layerId: 'commencementParking',
    mapLayerId: 'commencementEventParkingLayer',
    url: buildEventParkingLayerUrl(0) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function ConvocationHuntsmanParkingLayer() {
  useCustomLayer({
    layerId: 'convocationHuntsmanParking',
    mapLayerId: 'convocationHuntsmanEventParkingLayer',
    url: buildEventParkingLayerUrl(1) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function ConvocationKingsburyParkingLayer() {
  useCustomLayer({
    layerId: 'convocationKingsburyParking',
    mapLayerId: 'convocationKingsburyEventParkingLayer',
    url: buildEventParkingLayerUrl(16) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function ConvocationStudentUnionParkingLayer() {
  useCustomLayer({
    layerId: 'convocationStudentUnionParking',
    mapLayerId: 'convocationStudentUnionEventParkingLayer',
    url: buildEventParkingLayerUrl(17) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function UFootballParkingLayer() {
  const cfg = EVENT_PARKING_LAYER_CONFIG.find((c) => c.layerId === 'uFootballParking')!;
  const indices = Array.isArray(cfg.serviceLayerIndex) ? cfg.serviceLayerIndex : [cfg.serviceLayerIndex];
  const mapLayerIds = Array.isArray(cfg.mapLayerId) ? cfg.mapLayerId : [cfg.mapLayerId];

  useCustomLayer({
    layerId: 'uFootballParking',
    mapLayerId: mapLayerIds[0],
    url: buildEventParkingLayerUrl(indices[0]) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  useCustomLayer({
    layerId: 'uFootballParking',
    mapLayerId: mapLayerIds[1] ?? mapLayerIds[0],
    url: buildEventParkingLayerUrl(indices[1] ?? indices[0]) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function MonsterJamParkingLayer() {
  useCustomLayer({
    layerId: 'monsterJamParking',
    mapLayerId: 'monsterJamEventParkingLayer',
    url: buildEventParkingLayerUrl(12) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function SupercrossParkingLayer() {
  useCustomLayer({
    layerId: 'supercrossParking',
    mapLayerId: 'supercrossEventParkingLayer',
    url: buildEventParkingLayerUrl(13) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function StadiumConcertParkingLayer() {
  useCustomLayer({
    layerId: 'stadiumConcertParking',
    mapLayerId: 'stadiumConcertEventParkingLayer',
    url: buildEventParkingLayerUrl(4) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function UGymnasticsParkingLayer() {
  const cfg = EVENT_PARKING_LAYER_CONFIG.find((c) => c.layerId === 'uGymnasticsParking')!;
  const indices = Array.isArray(cfg.serviceLayerIndex) ? cfg.serviceLayerIndex : [cfg.serviceLayerIndex];
  const mapLayerIds = Array.isArray(cfg.mapLayerId) ? cfg.mapLayerId : [cfg.mapLayerId];

  useCustomLayer({
    layerId: 'uGymnasticsParking',
    mapLayerId: mapLayerIds[0],
    url: buildEventParkingLayerUrl(indices[0]) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  useCustomLayer({
    layerId: 'uGymnasticsParking',
    mapLayerId: mapLayerIds[1] ?? mapLayerIds[0],
    url: buildEventParkingLayerUrl(indices[1] ?? indices[0]) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function HsFootballParkingLayer() {
  useCustomLayer({
    layerId: 'hsFootballParking',
    mapLayerId: 'hsFootballEventParkingLayer',
    url: buildEventParkingLayerUrl(8) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function HsBasketballPlayoffParkingLayer() {
  useCustomLayer({
    layerId: 'hsBasketballPlayoffParking',
    mapLayerId: 'hsBasketballPlayoffEventParkingLayer',
    url: buildEventParkingLayerUrl(7) as string,
    outFields: ['*'],
    popupTemplate: eventParkingPopupTemplate,
    zoomOnToggle: false,
  });
  return null;
}

function EventToggleZoomController() {
  const mapView = useMapStore((state) => state.mapView);
  const layers = useLayerStore((state) => state.layers);
  const previousEventVisibility = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const newlyEnabledConfig =
      EVENT_PARKING_LAYER_CONFIG.find(
        (config) => Boolean(layers[config.layerId]) && !previousEventVisibility.current[config.layerId]
      ) ?? null;

    previousEventVisibility.current = EVENT_PARKING_LAYER_CONFIG.reduce<Record<string, boolean>>(
      (acc, config) => {
        acc[config.layerId] = Boolean(layers[config.layerId]);
        return acc;
      },
      {}
    );

    if (!newlyEnabledConfig || !mapView) {
      return;
    }

    const targetLayerId = getEventSearchMapLayerId(newlyEnabledConfig);
    const targetLayer = mapView.map?.findLayerById(targetLayerId) as FeatureLayer | undefined;

    if (!targetLayer) {
      return;
    }

    let cancelled = false;

    targetLayer
      .when()
      .then(() => targetLayer.queryExtent())
      .then(({ extent }) => {
        if (cancelled || !extent || !useLayerStore.getState().layers[newlyEnabledConfig.layerId]) {
          return;
        }

        return mapView.goTo(extent.expand(EVENT_TOGGLE_ZOOM_EXPAND), { duration: 600 });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [layers, mapView]);

  return null;
}

function EventParkingLayerController() {
  return (
    <>
      <WbbParkingLayer />
      <MbbParkingLayer />
      <CommencementParkingLayer />
      <ConvocationHuntsmanParkingLayer />
      <ConvocationKingsburyParkingLayer />
      <ConvocationStudentUnionParkingLayer />
      <UFootballParkingLayer />
      <MonsterJamParkingLayer />
      <SupercrossParkingLayer />
      <StadiumConcertParkingLayer />
      <UGymnasticsParkingLayer />
      <HsFootballParkingLayer />
      <HsBasketballPlayoffParkingLayer />
    </>
  );
}

function App() {
  const setEnabledLayers = useEventVisibilityStore((state) => state.setEnabledLayers);

  useEffect(() => {
    if (DEV_ENABLE_ALL_EVENTS) {
      const allEnabled = EVENT_PARKING_LAYER_CONFIG.reduce<Record<string, boolean>>(
        (acc, cfg) => { acc[cfg.layerId] = true; return acc; },
        {}
      );
      setEnabledLayers(allEnabled);
      return;
    }

    fetchActiveEventNames()
      .then((activeNames) => {
        const resolved = resolveEnabledLayersFromActiveNames(activeNames);
        setEnabledLayers(resolved);
      })
      .catch((err) => {
        console.error('Failed to fetch active event names from Event Names table:', err);
      });
  }, [setEnabledLayers]);

  return (
    <div className="h-full w-full relative">
      <UMapBase showBasemapGallery={true}>
        <Header
            searchWidgetPlaceholder={"Search UPark"}
            sourceConfig={{
              buildings: true,
              departments: true,
              gatheringSpaces: true,
              food: false,
              emergencyAssemblyPoints: false,
              addresses: true,
              buildingAliases: true
            }}
        />
        <UParkSidebar />
        <ParkingLotController />
        <ParkingSearchSource />
        <EventParkingSearchSources />
        <EventParkingLayerController />
        <EventToggleZoomController />
        <EventParkingPanel />
      </UMapBase>
    </div>
  );
}

export default App;
